/**
 * In-memory `ProjectStore`.
 *
 * Used by unit tests, by SSR/prerender passes where no storage exists, and as
 * the final fallback in `createBestAvailableStore` so the editor still works
 * (for the length of the session) on a locked-down browser.
 */
import type { CreateProjectInput, ProjectStore } from './ProjectStore';
import type { ProjectRecord, ProjectSummary } from './types';
import {
  buildDuplicate,
  buildRecord,
  cloneRecord,
  normalizeName,
  nowIso,
  summarize,
} from './records';

export function createMemoryStore(seed: readonly ProjectRecord[] = []): ProjectStore {
  const records = new Map<string, ProjectRecord>();
  for (const record of seed) records.set(record.id, cloneRecord(record));

  async function list(): Promise<ProjectSummary[]> {
    return summarize([...records.values()]);
  }

  async function get(id: string): Promise<ProjectRecord | null> {
    const found = records.get(id);
    return found ? cloneRecord(found) : null;
  }

  async function put(record: ProjectRecord): Promise<void> {
    records.set(record.id, cloneRecord(record));
  }

  async function create(input: CreateProjectInput): Promise<ProjectRecord> {
    const record = buildRecord(input);
    records.set(record.id, cloneRecord(record));
    return record;
  }

  async function rename(id: string, name: string): Promise<ProjectRecord | null> {
    const existing = records.get(id);
    if (!existing) return null;
    const updated: ProjectRecord = {
      ...cloneRecord(existing),
      name: normalizeName(name),
      updatedAt: nowIso(),
    };
    records.set(id, updated);
    return cloneRecord(updated);
  }

  async function duplicate(id: string): Promise<ProjectRecord | null> {
    const existing = records.get(id);
    if (!existing) return null;
    const copy = buildDuplicate(
      existing,
      [...records.values()].map((entry) => entry.name),
    );
    records.set(copy.id, cloneRecord(copy));
    return copy;
  }

  async function remove(id: string): Promise<void> {
    records.delete(id);
  }

  async function search(query: string): Promise<ProjectSummary[]> {
    return summarize([...records.values()], query);
  }

  async function clear(): Promise<void> {
    records.clear();
  }

  return { list, get, put, create, rename, duplicate, remove, search, clear, kind: 'memory' };
}
